import { type Dispatch, type MutableRefObject, type SetStateAction, useCallback } from "react";
import { createNewLead } from "@/lib/lead/newLeads";
import { isMeaningful } from "@/lib/lead/utils/isMeaningful";
import type { Lead } from "../../types/lead";
import type { Location } from "../../types/location";
import { DeepgramStreamingEngine } from "@/lib/asr/deepgramStreamingEngine";

type ExtractionLike = {
  cancel: () => void;
  flush: () => Promise<void>;
};

type MergeSuggestion = {
  sourceId: string;
  reason: string;
};

type UseLeadAsrFlowParams = {
  asrEngineRef: MutableRefObject<DeepgramStreamingEngine | null>;
  listeningRef: MutableRefObject<boolean>;
  currentLeadRef: MutableRefObject<Lead | null>;
  transcriptRef: MutableRefObject<string>;
  location: Location | null;
  extraction: ExtractionLike;
  isStoppingUi: boolean;
  emptyLeadAutoDeleteEnabled: boolean;
  purgeLeads: (ids: string[]) => Promise<any>;
  deleteLead: (id: string) => void;
  setIsStoppingUi: Dispatch<SetStateAction<boolean>>;
  setIsListening: Dispatch<SetStateAction<boolean>>;
  setCurrentLead: Dispatch<SetStateAction<Lead | null>>;
  setLeads: Dispatch<SetStateAction<Lead[]>>;
  setTranscript: Dispatch<SetStateAction<string>>;
  setMergeSuggestion: Dispatch<SetStateAction<MergeSuggestion | null>>;
  resetCoachState: () => void;
  coachIfNeeded: (lead: Lead) => void;
  findMergeSuggestionForCurrent: () => MergeSuggestion | null;
  clearAsrStatusUi?: () => void;
};

export function useLeadAsrFlow(params: UseLeadAsrFlowParams) {
  const clearTranscript = useCallback(
    (reason: string) => {
      console.log("[transcript:clear]", reason);
      params.setTranscript("");
      params.transcriptRef.current = "";
    },
    [params]
  );

  const startRecording = useCallback(async () => {
    if (params.listeningRef.current) return;
    const eng = params.asrEngineRef.current;
    if (!eng) return;
    if (!eng.isInitialized()) {
      await eng.initModel();
    }
    params.setIsListening(true);
    params.listeningRef.current = true;
    await eng.start();
  }, [params]);

  const startNewLead = useCallback(() => {
    params.asrEngineRef.current?.reset();
    const lead = createNewLead({
      originLocationId: params.location?.id ?? null,
      originLocationName: params.location?.name ?? null,
    });
    params.setCurrentLead(lead);
    params.currentLeadRef.current = lead;
    params.setLeads((prev) => [lead, ...prev]);
    clearTranscript("startNewLead");
    params.extraction.cancel();
    params.resetCoachState();
    params.setMergeSuggestion(null);
    params.setIsListening(false);
    params.listeningRef.current = false;
    params.clearAsrStatusUi?.();
  }, [clearTranscript, params]);

  const stopListening = useCallback(async () => {
    if (params.isStoppingUi) return;
    params.setIsStoppingUi(true);
    try {
      const eng = params.asrEngineRef.current;
      if (!eng) {
        params.setIsListening(false);
        params.listeningRef.current = false;
        return;
      }
      params.setIsListening(false);
      params.listeningRef.current = false;
      await eng.stop();
      await params.extraction.flush();
      const lead = params.currentLeadRef.current;
      if (lead) {
        params.coachIfNeeded(lead);
        params.setMergeSuggestion(params.findMergeSuggestionForCurrent());
      }
    } catch (err) {
      console.error("[asr] stop failed", err);
    } finally {
      params.setIsStoppingUi(false);
    }
  }, [params]);

  const closeCurrentLead = useCallback(async () => {
    const leadToMaybeDelete = params.currentLeadRef.current;
    const transcriptWas = params.transcriptRef.current;
    try {
      if (params.listeningRef.current) {
        const eng = params.asrEngineRef.current;
        if (eng) {
          params.setIsListening(false);
          params.listeningRef.current = false;
          await eng.stop();
          await params.extraction.flush();
        }
      }
    } catch (err) {
      console.error("[asr] closeCurrentLead stop failed", err);
    }

    if (params.emptyLeadAutoDeleteEnabled && leadToMaybeDelete) {
      const hasTranscript = typeof transcriptWas === "string" && transcriptWas.trim().length > 0;
      const hasExtracted = isMeaningful(leadToMaybeDelete.extracted);
      const isSent = leadToMaybeDelete.sent === true;
      const isRenamed = leadToMaybeDelete.name !== "New Customer";
      const isOldEnough = Date.now() - leadToMaybeDelete.lastUpdated > 30_000;
      if (!hasTranscript && !hasExtracted && !isSent && !isRenamed && isOldEnough) {
        void params.purgeLeads([leadToMaybeDelete.id]);
        params.deleteLead(leadToMaybeDelete.id);
      }
    }

    params.setCurrentLead(null);
    params.currentLeadRef.current = null;
    clearTranscript("closeCurrentLead");
    params.extraction.cancel();
    params.resetCoachState();
    params.setMergeSuggestion(null);
    params.clearAsrStatusUi?.();
  }, [clearTranscript, params]);

  const continueListening = useCallback(() => {
    const lead = params.currentLeadRef.current;
    if (!lead) return;
    void startRecording();
  }, [params, startRecording]);

  return {
    clearTranscript,
    startNewLead,
    stopListening,
    closeCurrentLead,
    continueListening,
  };
}
